import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Archive, Eye, Download, Film, Loader2, RefreshCw, Inbox,
} from "lucide-react";
import {
  updateContentStatus, getStatusColor, canRender, canExport, canArchive,
  type ContentStatus,
} from "@/lib/contentLifecycle";
import { enqueueRender, fetchLatestJobForAsset, type RenderJob } from "@/lib/renderJobs";
import { RenderJobIndicator } from "./RenderJobIndicator";
import { RenderedAssetPlayer } from "./RenderedAssetPlayer";
import {
  generateSimulationContent, type ContentFormat, type SimulationContent,
} from "@/lib/contentGenerator";
import { CONTENT_ASSETS_CHANGED_EVENT } from "@/lib/contentTelemetry";
import { ASSET_PREDICTIONS_UPDATED_EVENT } from "@/lib/predictionEngine";
import { toast } from "sonner";

interface PredictionRow {
  asset_id: string;
  predicted_virality_score: number;
  predicted_replay_rate: number;
  predicted_completion_rate: number;
  predicted_save_rate: number;
  confidence: number;
  dominant_signal: string | null;
}

interface AssetRow {
  id: string;
  hook: string | null;
  scenario_id: string;
  scenario_title: string;
  archetype: string;
  category: string;
  format: string;
  platform: string;
  status: string;
  cta: string | null;
  created_at: string;
  updated_at?: string | null;
}

const FILTERS = [
  { id: "all", label: "All" },
  { id: "generated", label: "Generated" },
  { id: "rendering", label: "Rendering" },
  { id: "rendered", label: "Rendered" },
  { id: "exported", label: "Exported" },
  { id: "archived", label: "Archived" },
] as const;

type FilterId = (typeof FILTERS)[number]["id"];

export default function GeneratedAssetsQueue() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [predictions, setPredictions] = useState<Record<string, PredictionRow>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterId>("all");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data, error }, { data: pdata }] = await Promise.all([
      (supabase as any)
        .from("content_assets")
        .select("id, hook, scenario_id, scenario_title, archetype, category, format, platform, status, cta, created_at, updated_at")
        .order("created_at", { ascending: false })
        .limit(200),
      (supabase as any)
        .from("asset_predictions")
        .select("asset_id, predicted_virality_score, predicted_replay_rate, predicted_completion_rate, predicted_save_rate, confidence, dominant_signal")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);
    if (!error && data) setAssets(data as unknown as AssetRow[]);
    if (pdata) {
      const map: Record<string, PredictionRow> = {};
      (pdata as PredictionRow[]).forEach((p) => { map[p.asset_id] = p; });
      setPredictions(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Listen for cross-component refresh signals (new bundle / new predictions).
  useEffect(() => {
    const handler = () => { void load(); };
    window.addEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
    window.addEventListener(ASSET_PREDICTIONS_UPDATED_EVENT, handler);
    return () => {
      window.removeEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
      window.removeEventListener(ASSET_PREDICTIONS_UPDATED_EVENT, handler);
    };
  }, [load]);

  // ---------- Optimistic local state ----------
  const patchLocal = useCallback((id: string, patch: Partial<AssetRow>) => {
    setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  // ---------- Lifecycle actions ----------
  // Real render: enqueue a render_jobs row. The external Node worker
  // (see /render-worker) picks it up, runs Remotion, uploads the MP4 +
  // thumbnail to the `renders` Storage bucket, and writes back state.
  // RenderJobIndicator subscribes to render_jobs realtime for live progress.
  const handleRender = useCallback(async (a: AssetRow) => {
    if (!canRender(a.status)) return;
    setBusyId(a.id);
    patchLocal(a.id, { status: "rendering" });
    const r = await enqueueRender({ id: a.id, scenario_id: a.scenario_id, format: a.format });
    if (!r.ok) {
      patchLocal(a.id, { status: a.status });
      toast.error(`Render queue failed — ${(r as { ok: false; error: string }).error}`);
    } else {
      toast.success("Render queued — worker will pick it up");
    }
    setBusyId(null);
  }, [patchLocal]);

  const handleExport = useCallback(async (a: AssetRow) => {
    if (!canExport(a.status)) return;
    setBusyId(a.id);
    const prev = a.status;
    patchLocal(a.id, { status: "exported" });
    const r = await updateContentStatus(a.id, "exported", { from: prev });
    if (!r.success) {
      patchLocal(a.id, { status: prev });
      toast.error("Export failed");
    }
    setBusyId(null);
  }, [patchLocal]);

  const handleArchive = useCallback(async (a: AssetRow) => {
    if (!canArchive(a.status)) return;
    setBusyId(a.id);
    const prev = a.status;
    patchLocal(a.id, { status: "archived" });
    const r = await updateContentStatus(a.id, "archived", { from: prev });
    if (!r.success) {
      patchLocal(a.id, { status: prev });
      toast.error("Archive failed");
    }
    setBusyId(null);
  }, [patchLocal]);

  // ---------- Filter ----------
  const visible = useMemo(() => {
    if (filter === "all") return assets;
    return assets.filter((a) => a.status === filter);
  }, [assets, filter]);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="space-y-3">
        <div className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg flex items-center gap-2">
            <Inbox className="w-5 h-5 text-cyan" />
            Generated Assets Queue
            <Badge
              variant="outline"
              className="ml-2 text-[10px] uppercase tracking-wider border-cyan/40 text-cyan font-mono"
            >
              {visible.length} / {assets.length}
            </Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            className="text-cyan hover:text-cyan hover:bg-cyan/10 h-8"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        {/* Filter rail */}
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`text-[10px] uppercase tracking-wider font-mono px-2.5 py-1 rounded border transition-colors ${
                  active
                    ? "border-cyan/60 bg-cyan/10 text-cyan"
                    : "border-glass-border text-muted-foreground hover:text-foreground hover:border-cyan/30"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading && assets.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading inventory…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 text-xs font-mono text-muted-foreground">
            {assets.length === 0
              ? "No assets generated yet. Use the panel above to seed the queue."
              : "No assets match this filter."}
          </div>
        ) : (
          <ScrollArea className="h-[520px]">
            <div className="divide-y divide-glass-border">
              {visible.map((a) => {
                const isBusy = busyId === a.id;
                return (
                  <div key={a.id} className="p-3 md:p-4 hover:bg-cyan/5 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      {/* Left — meta */}
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`text-[10px] uppercase tracking-wider font-mono ${getStatusColor(a.status)}`}
                          >
                            {a.status}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="text-[10px] uppercase tracking-wider font-mono border-glass-border text-foreground/80"
                          >
                            {a.format}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="text-[10px] uppercase tracking-wider font-mono border-glass-border text-muted-foreground"
                          >
                            {a.platform}
                          </Badge>
                          <span className="text-[10px] font-mono text-muted-foreground ml-auto md:ml-0">
                            {new Date(a.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-foreground leading-snug">
                          {a.hook || "—"}
                        </div>
                        <div className="text-[11px] font-mono text-muted-foreground truncate">
                          {a.scenario_title}
                        </div>
                        <PredictiveOverlay p={predictions[a.id]} />
                        <RenderJobIndicator assetId={a.id} />
                      </div>

                      {/* Right — actions */}
                      <div className="flex flex-wrap items-center gap-1.5 md:flex-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/viral/assets/${a.id}`)}
                          className="h-7 px-2 text-xs text-cyan hover:text-cyan hover:bg-cyan/10"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isBusy || !canRender(a.status)}
                          onClick={() => handleRender(a)}
                          className="h-7 px-2 text-xs text-emerald-300 hover:text-emerald-200 hover:bg-emerald-400/10 disabled:opacity-30"
                        >
                          {isBusy && a.status === "rendering" ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : (
                            <Film className="w-3.5 h-3.5 mr-1" />
                          )}
                          Render
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isBusy || !canExport(a.status)}
                          onClick={() => handleExport(a)}
                          className="h-7 px-2 text-xs text-amber-300 hover:text-amber-200 hover:bg-amber-400/10 disabled:opacity-30"
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> Export
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isBusy || !canArchive(a.status)}
                          onClick={() => handleArchive(a)}
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 disabled:opacity-30"
                        >
                          <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>

      
    </Card>
  );
}

// ---------- View modal (read-only forensic preview) ----------

function ViewAssetDialog({ asset, onClose }: { asset: AssetRow | null; onClose: () => void }) {
  const preview = useMemo<SimulationContent | null>(() => {
    if (!asset) return null;
    try {
      return generateSimulationContent({
        scenarioId: asset.scenario_id,
        format: asset.format as ContentFormat,
      });
    } catch {
      return null;
    }
  }, [asset]);

  if (!asset) return null;

  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-charcoal-medium border-glass-border text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Eye className="w-4 h-4 text-cyan" />
            Asset Preview
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-mono ${getStatusColor(asset.status)}`}>
              {asset.status}
            </Badge>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-mono border-glass-border text-foreground/80">
              {asset.format}
            </Badge>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-mono border-glass-border text-muted-foreground">
              {asset.platform}
            </Badge>
            <span className="text-[10px] font-mono text-muted-foreground ml-auto">
              {new Date(asset.created_at).toLocaleString()}
            </span>
          </div>

          <Section label="Hook">
            <p className="text-sm text-foreground leading-snug">{asset.hook || "—"}</p>
          </Section>

          <Section label="Scenario">
            <p className="text-sm text-foreground">{asset.scenario_title}</p>
            <p className="text-[11px] font-mono text-muted-foreground mt-1">
              {asset.archetype} · {asset.category}
            </p>
          </Section>

          <Section label="Dialogue Preview">
            {preview && preview.dialogue.length > 0 ? (
              <div className="space-y-1.5 font-mono text-[12px]">
                {preview.dialogue.slice(0, 6).map((t, i) => (
                  <div key={i} className="flex gap-2">
                    <span
                      className={`uppercase tracking-wider shrink-0 w-12 ${
                        t.role === "them" ? "text-red-400/80" : "text-cyan/80"
                      }`}
                    >
                      {t.role === "them" ? "Them" : "You"}
                    </span>
                    <span className="text-foreground/90">{t.text}</span>
                  </div>
                ))}
                {preview.dialogue.length > 6 && (
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground pt-1">
                    + {preview.dialogue.length - 6} more turns
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs font-mono text-muted-foreground">Dialogue unavailable.</p>
            )}
          </Section>

          <Section label="CTA">
            <p className="text-sm text-foreground italic">
              {asset.cta || "Practice the conversation before it happens."}
            </p>
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-glass-border pt-3">
      <div className="text-[10px] uppercase tracking-wider font-mono text-cyan mb-1.5">{label}</div>
      {children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Predictive Signal Overlay — pre-render behavioral forecast
// ────────────────────────────────────────────────────────────────
function PredictiveOverlay({ p }: { p: PredictionRow | undefined }) {
  if (!p) {
    return (
      <div className="mt-2 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">
        <span className="w-1 h-1 rounded-full bg-muted-foreground/60 animate-pulse" />
        Forecast pending
      </div>
    );
  }
  const v = p.predicted_virality_score;
  const replayLabel =
    p.predicted_replay_rate >= 75 ? "HIGH" :
    p.predicted_replay_rate >= 50 ? "MED"  : "LOW";
  const viralityTone =
    v >= 75 ? "border-amber-400/60 text-amber-300 bg-amber-400/10" :
    v >= 50 ? "border-cyan/50 text-cyan bg-cyan/10" :
              "border-muted-foreground/40 text-muted-foreground bg-muted-foreground/5";
  const confTone =
    p.confidence >= 80 ? "text-emerald-300" :
    p.confidence >= 60 ? "text-cyan" : "text-amber-300";

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono uppercase tracking-wider">
      <span className={`px-1.5 py-0.5 rounded border ${viralityTone}`}>
        VIRALITY: <span className="tabular-nums">{v}</span>
      </span>
      <span className="text-muted-foreground">
        REPLAY: <span className={
          replayLabel === "HIGH" ? "text-amber-300" :
          replayLabel === "MED"  ? "text-cyan" : "text-foreground/70"
        }>{replayLabel}</span>
      </span>
      <span className="text-muted-foreground">
        COMPLETION: <span className="text-foreground/90 tabular-nums">{p.predicted_completion_rate}%</span>
      </span>
      <span className="text-muted-foreground">
        CONFIDENCE: <span className={`${confTone} tabular-nums`}>{p.confidence}%</span>
      </span>
      {p.dominant_signal && (
        <span className="text-cyan/80">
          SIGNAL: <span className="text-cyan">{p.dominant_signal}</span>
        </span>
      )}
    </div>
  );
}
